import React, { useState, useEffect, useCallback } from 'react'
import { api, FOODSBY_BASE_URL } from '../../../api/api'
import { Action, Button, Spinner } from '@foodsby/nutrient'
import SimpleSpinner from '../../common/SimpleSpinner'
import currency from 'currency.js'
import format from 'date-fns/format'
import ModalPopup from '../../common/ModalPopup'

const SUBSCRIPTION_STATE = Object.freeze({
  SUBSCRIBED: 1,
  CANCEL_REQUESTED: 2,
  UNSUBSCRIBED: 3,
})

const Subscribed = ({
  userSubscription,
  getUserSubscription,
  showCancelPopup,
  setShowCancelPopup,
}) => {
  const [cancellationLoading, setCancellationLoading] = useState(false)
  const [subscriptionCancelled, setSubscriptionCancelled] = useState(false)

  // Api
  const cancelUserSubscription = async () => {
    setCancellationLoading(true)
    await api.post(
      `${FOODSBY_BASE_URL}/api/v1/credit-cards/subscriptions/${userSubscription?.subscriptionId}/cancel`,
    )
    setCancellationLoading(false)
    setSubscriptionCancelled(true)
    getUserSubscription()
    setShowCancelPopup(false)
  }

  return (
    <p>
      Your subscription will auto renew for{' '}
      {currency(userSubscription?.subscriptionFee / 100, {
        formatWithSymbol: true,
      }).format()}{' '}
      on {format(userSubscription?.nextPaymentDate, 'MM/DD/YYYY')}.
      {showCancelPopup && (
        <ModalPopup
          onCancel={() => {
            setShowCancelPopup(false)
            if (subscriptionCancelled) {
              getUserSubscription()
            }
          }}
        >
          <>
            {!cancellationLoading && !subscriptionCancelled ? (
              <>
                <h5 className="SubscriptionUpgrade-header">
                  Are you sure you want to unsubscribe from Unlimited Delivery?
                </h5>
                <p>
                  You will continue to receive free delivery until{' '}
                  {format(userSubscription?.nextPaymentDate, 'MM/DD/YYYY')}.
                </p>
                <div className="SubscriptionUpgrade-cta row end-xs">
                  <div className="col-xs-12">
                    <Action
                      onClick={() => setShowCancelPopup(false)}
                      style={{ marginRight: '32px' }}
                    >
                      No, never mind
                    </Action>
                    <Button
                      disabled={cancellationLoading || subscriptionCancelled}
                      onClick={cancelUserSubscription}
                      type="submit"
                    >
                      Yes, Unsubscribe
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <SimpleSpinner size="sm" />
            )}
          </>
        </ModalPopup>
      )}
    </p>
  )
}

const CancelRequested = ({ userSubscription }) => {
  return (
    <p>
      Your cancellation request has been received. You will continue to receive
      unlimited delivery until{' '}
      {format(userSubscription?.nextPaymentDate, 'MM/DD/YYYY')}.
    </p>
  )
}

const Unsubscribed = ({ userSubscription }) => {
  return (
    <p>
      Your subscription expired on{' '}
      {format(userSubscription?.nextPaymentDate, 'MM/DD/YYYY')}. You can
      resubscribe any time at checkout.
    </p>
  )
}

export const SubscriptionUpgrade = ({ loading, setLoading, setHidden }) => {
  // States
  const [userSubscription, setUserSubscription] = useState(null)
  const [subscriptionState, setSubscriptionState] = useState()
  const [showCancelPopup, setShowCancelPopup] = useState(false)

  // Api
  const getUserSubscription = useCallback(async () => {
    setLoading(true)
    try {
      const sub = await api.get(
        `${FOODSBY_BASE_URL}/api/v1/credit-cards/subscriptions`,
      )
      setUserSubscription(sub[0])
      if (sub[0] == null) {
        setHidden(true)
      } else {
        setHidden(false)
      }
    } catch (e) {
      setUserSubscription({})
    } finally {
      setLoading(false)
    }
  }, [setHidden, setLoading])

  // Effects
  useEffect(() => {
    if (!loading && userSubscription === null) {
      getUserSubscription()
    }
  }, [getUserSubscription, loading, setLoading, userSubscription])

  useEffect(() => {
    const status = Object.keys(SUBSCRIPTION_STATE).find(
      s => s === userSubscription?.status,
    )
    setSubscriptionState(SUBSCRIPTION_STATE[status])
  }, [subscriptionState, userSubscription])

  return (
    <>
      <div className="HeaderSection">
        <h6 className="HeaderSection-heading">
          Unlimited Delivery{' '}
          {loading ? <Spinner className="primary" size="xs" /> : null}
        </h6>
        {subscriptionState === SUBSCRIPTION_STATE.SUBSCRIBED && (
          <Action
            onClick={() => setShowCancelPopup(true)}
            style={{
              color: 'var(--nu-secondary)',
            }}
          >
            Unsubscribe
          </Action>
        )}
      </div>

      {subscriptionState === SUBSCRIPTION_STATE.SUBSCRIBED ? (
        <Subscribed
          getUserSubscription={getUserSubscription}
          setShowCancelPopup={setShowCancelPopup}
          showCancelPopup={showCancelPopup}
          userSubscription={userSubscription}
        />
      ) : subscriptionState === SUBSCRIPTION_STATE.UNSUBSCRIBED ? (
        <Unsubscribed userSubscription={userSubscription} />
      ) : subscriptionState === SUBSCRIPTION_STATE.CANCEL_REQUESTED ? (
        <CancelRequested userSubscription={userSubscription} />
      ) : null}
    </>
  )
}

export default SubscriptionUpgrade
